import { useApp } from '@/context/AppContext';
import { StatusBadge } from '@/components/ui/Badge';
import { formatDistance } from '@/hooks/useGeofencing';

export default function MyDay() {
  const { todayStops, custById, setActiveStopId, setMobileScreen, geo, hasPlugin, team, viewingTech, setViewingTech, userEmail } = useApp();

  const myStops = viewingTech ? todayStops.filter(s => s.tech === viewingTech) : todayStops;
  const done    = myStops.filter(s => s.status === 'done').length;
  const left    = myStops.length - done;
  const active  = myStops.find(s => s.status === 'active');

  // Greeting name: prefer the viewing tech's first name; fall back to user email local-part.
  const greetingName = (() => {
    if (viewingTech) return viewingTech.split(/[ ,]/)[0];
    if (userEmail) {
      const local = userEmail.split('@')[0];
      return local.charAt(0).toUpperCase() + local.slice(1);
    }
    return 'there';
  })();

  const geofencingOn = hasPlugin('geofencing');
  const nextStop = active || myStops.find(s => s.status === 'pending');
  const nextDistance = nextStop && geo?.distances?.get?.(nextStop.id);

  const goToStop = (stop) => {
    setActiveStopId(stop.id);
    setMobileScreen('stop');
  };

  return (
    <div className="mobile-page">
      {/* Hero header */}
      <div
        className="mobile-hero"
        style={{ margin: '-14px -16px 0', padding: '14px 16px 16px' }}
      >
        <div style={{ fontSize: 12.5, color: 'var(--t3)' }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '2px 0 12px' }}>
          <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--t1)' }}>
            Welcome, {greetingName}
          </div>
          {team.length > 0 && (
            <select
              value={viewingTech || ''}
              onChange={(e) => setViewingTech(e.target.value || null)}
              style={{
                fontSize: 11,
                padding: '4px 6px',
                borderRadius: 6,
                background: 'var(--s2)',
                border: '1px solid var(--b1)',
                color: 'var(--t2)',
                maxWidth: 120,
              }}
              title="View as tech"
            >
              <option value="">All techs</option>
              {team.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="m-hstat">
            <div className="m-hstat-v" style={{ color: 'var(--teal)' }}>{done}</div>
            <div className="m-hstat-l">Done</div>
          </div>
          <div className="m-hstat">
            <div className="m-hstat-v" style={{ color: 'var(--cyan)' }}>{left}</div>
            <div className="m-hstat-l">Left</div>
          </div>
          <div className="m-hstat">
            <div className="m-hstat-v" style={{ color: 'var(--amber)' }}>—</div>
            <div className="m-hstat-l">Est. End</div>
          </div>
        </div>
      </div>

      {/* Active stop banner */}
      {active && (
        <div style={{
          padding: '10px 12px',
          borderRadius: 10,
          background: 'rgba(0,212,255,.08)',
          border: '1px solid rgba(0,212,255,.2)',
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
        }}>
          <span style={{ fontSize: 16 }}>⚡</span>
          <div style={{ fontSize: 12, color: 'var(--t2)', lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--cyan)' }}>Active stop:</strong>{' '}
            {custById(active.custId)?.name || ''} — {custById(active.custId)?.addr || ''}
          </div>
        </div>
      )}

      {/* Geofencing distance banner */}
      {geofencingOn && nextStop && !active && (
        <div style={{
          padding: '10px 12px',
          borderRadius: 10,
          background: 'var(--s2)',
          border: '1px solid var(--b1)',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          marginTop: 8,
        }}>
          <span style={{ fontSize: 16 }}>📍</span>
          <div style={{ fontSize: 12, color: 'var(--t2)', flex: 1 }}>
            <div><strong style={{ color: 'var(--t1)' }}>Next:</strong> {custById(nextStop.custId)?.name || ''}</div>
            {geo?.error && (
              <div style={{ fontSize: 10.5, color: 'var(--rose)', marginTop: 2 }}>{geo.error}</div>
            )}
            {!geo?.error && geo?.permission === 'denied' && (
              <div style={{ fontSize: 10.5, color: 'var(--amber)', marginTop: 2 }}>
                Location permission denied — enable in browser settings for arrival alerts
              </div>
            )}
            {!geo?.error && nextDistance != null && (
              <div style={{ fontSize: 10.5, color: 'var(--t3)', marginTop: 2 }}>
                {formatDistance(nextDistance)} away
              </div>
            )}
            {!geo?.error && geo?.permission !== 'denied' && nextDistance == null && (
              <div style={{ fontSize: 10.5, color: 'var(--t3)', marginTop: 2 }}>
                Waiting for location…
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stop list */}
      <div className="m-card">
        <div style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--b1)',
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--t1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          Today's Route
          <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--t3)' }}>{myStops.length} stops</span>
        </div>
        {myStops.map((stop, i) => {
          const c = custById(stop.custId);
          const numStyle = stop.status === 'done' ? 'sn-done' : stop.status === 'active' ? 'sn-active' : 'sn-next';
          const numLabel = stop.status === 'done' ? '✓' : stop.status === 'active' ? '▶' : i + 1;
          return (
            <div key={stop.id} className="m-stop" onClick={() => goToStop(stop)}>
              <div className={`m-stop-num ${numStyle}`}>{numLabel}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--t1)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {c?.name || ''}
                </div>
                <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 1 }}>{c?.addr || ''}</div>
                {c?.gateCode && (
                  <div style={{ fontSize: 10.5, color: 'var(--t3)', marginTop: 2 }}>{c.gateCode}</div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                <div style={{ fontSize: 11, color: 'var(--t3)', fontWeight: 600 }}>{stop.time}</div>
                <StatusBadge status={stop.status} />
                {geofencingOn && stop.status !== 'done' && geo?.distances?.get?.(stop.id) != null && (
                  <div style={{ fontSize: 10, color: 'var(--cyan)', fontWeight: 600 }}>
                    {formatDistance(geo.distances.get(stop.id))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
